import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Globe, Sparkles, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * TranslateAll — mass translation utility.
 *
 * Fires a background job on the backend that iterates over Cases, Insights
 * and Jobs, translating standard fields from PT into EN + ES via the same
 * Claude-based translator used by per-doc translate buttons.
 *
 * Only translates missing fields by default (idempotent + cost-conscious).
 * Polls /admin/translate-all/status every 3s while a job is running.
 */
const COLLECTIONS = [
  { key: "cases", label: "Cases" },
  { key: "insights", label: "Insights" },
  { key: "jobs", label: "Vagas (Carreiras)" },
];
const LANGS = [
  { key: "en", label: "Inglês (US)" },
  { key: "es", label: "Espanhol (LatAm)" },
];

/** Notificação nativa do navegador — funciona mesmo com a aba em background,
 *  então dá pra iniciar a tradução e ir fazer outra coisa. */
function notifyDesktop(job) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  const ok = job.status === "done";
  new Notification(ok ? "Places CMS — Tradução concluída" : "Places CMS — Tradução falhou", {
    body: ok
      ? `${job.done || 0} campos traduzidos em ${(job.collections || []).join(", ")}.`
      : String(job.error || "Veja os detalhes no painel."),
    icon: "/favicon.ico",
  });
}

export default function TranslateAll() {
  const [colls, setColls] = useState(COLLECTIONS.map(c => c.key));
  const [langs, setLangs] = useState(LANGS.map(l => l.key));
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/translate-all/status");
      setStatus((prev) => {
        // Alerta de conclusão: dispara quando o job sai de "running" para
        // "done"/"error" — o usuário não precisa ficar olhando o painel.
        if (prev?.status === "running" && data?.status && data.status !== "running") {
          if (data.status === "done") {
            toast.success(`Tradução em massa concluída — ${data.done || 0} campos processados.`, { duration: 15000 });
          } else {
            toast.error(`Tradução em massa falhou: ${data.error || "erro desconhecido"}`, { duration: 20000 });
          }
          notifyDesktop(data);
        }
        return data;
      });
      return data;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    loadStatus();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (status?.status === "running") {
      if (!pollRef.current) pollRef.current = setInterval(loadStatus, 3000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [status?.status]);

  const start = async () => {
    if (!colls.length || !langs.length) { toast.error("Selecione ao menos uma coleção e um idioma."); return; }
    if (!window.confirm(
      `Isso vai traduzir ${colls.join(" + ")} para ${langs.join(" + ")}.\n\n` +
      (onlyMissing ? "Só campos ainda não traduzidos serão processados (idempotente)." : "TODOS os campos serão reprocessados — consome mais créditos LLM.") +
      "\n\nProsseguir?"
    )) return;
    setStarting(true);
    try {
      if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
      const { data } = await api.post("/admin/translate-all", {
        collections: colls, target_langs: langs, only_missing: onlyMissing, preserve_html: true,
      });
      if (!data.ok && data.job) {
        toast.info("Já existe uma tradução em andamento.");
        setStatus(data.job);
      } else {
        toast.success("Tradução iniciada. Você pode fechar esta aba — o job continua no servidor.");
        await loadStatus();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao iniciar.");
    } finally { setStarting(false); }
  };

  const pct = status?.total ? Math.min(100, Math.round((status.done / status.total) * 100)) : 0;
  const isRunning = status?.status === "running";
  const isDone = status?.status === "done";
  const isError = status?.status === "error";

  return (
    <div data-testid="translate-all-page">
      <div className="flex items-center gap-3 mb-2">
        <Globe className="w-6 h-6 text-accent-orange" />
        <h1 className="font-display text-4xl">Traduzir Tudo</h1>
      </div>
      <p className="text-sm text-neutral-500 mb-8 max-w-2xl">
        Varre Cases, Insights e Vagas, e preenche as traduções EN/ES a partir do PT usando Claude Sonnet (Emergent LLM).
        Por padrão só processa campos que ainda estão vazios — é seguro reexecutar (idempotente + econômico em créditos).
      </p>

      <div className="border border-black/10 rounded-md p-5 mb-6 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Coleções</div>
          <div className="flex flex-wrap gap-2">
            {COLLECTIONS.map(c => (
              <label key={c.key} className={`text-sm px-3 py-1.5 rounded-full border cursor-pointer ${colls.includes(c.key) ? "bg-black text-white border-black" : "border-black/15"}`}>
                <input type="checkbox" checked={colls.includes(c.key)} onChange={(e) => setColls(e.target.checked ? [...colls, c.key] : colls.filter(x => x !== c.key))} className="hidden" />
                {c.label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Idiomas de destino</div>
          <div className="flex flex-wrap gap-2">
            {LANGS.map(l => (
              <label key={l.key} className={`text-sm px-3 py-1.5 rounded-full border cursor-pointer ${langs.includes(l.key) ? "bg-accent-orange text-white border-accent-orange" : "border-black/15"}`}>
                <input type="checkbox" checked={langs.includes(l.key)} onChange={(e) => setLangs(e.target.checked ? [...langs, l.key] : langs.filter(x => x !== l.key))} className="hidden" />
                {l.label}
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} data-testid="translate-only-missing" />
          <span>Só traduzir campos ainda vazios <strong>(recomendado — economiza créditos)</strong></span>
        </label>
        <button onClick={start} disabled={starting || isRunning} data-testid="translate-all-start"
          className="bg-gradient-to-r from-accent-orange to-accent-yellow text-black font-semibold text-sm px-6 py-3 rounded-full hover:shadow-lg disabled:opacity-50 inline-flex items-center gap-2">
          {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {isRunning ? "Job em execução…" : "Iniciar tradução em massa"}
        </button>
      </div>

      {/* Status / progress */}
      {status && status.status !== "idle" && (
        <div className="border border-black/10 rounded-md p-5" data-testid="translate-status">
          <div className="flex items-center gap-2 mb-3">
            {isRunning && <Loader2 className="w-4 h-4 animate-spin text-accent-orange" />}
            {isDone && <CheckCircle2 className="w-4 h-4 text-green-600" />}
            {isError && <AlertTriangle className="w-4 h-4 text-red-500" />}
            <div className="text-sm font-semibold uppercase tracking-widest">
              {isRunning && "Traduzindo…"}
              {isDone && "Concluído"}
              {isError && "Falhou"}
            </div>
          </div>
          <div className="w-full h-2 bg-black/10 rounded-full overflow-hidden mb-2">
            <div className="h-full bg-accent-orange transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-xs text-neutral-500 font-mono">
            {status.done || 0} / {status.total || 0} campos ({pct}%) · Coleções: {(status.collections || []).join(", ")} · Idiomas: {(status.target_langs || []).join(", ")}
          </div>
          {status.started_at && <div className="text-[10px] text-neutral-400 mt-1">Iniciado em {status.started_at.slice(0, 19).replace("T", " ")}</div>}
          {isError && status.error && <div className="text-xs text-red-600 mt-3 font-mono">{status.error}</div>}
          {Array.isArray(status.errors) && status.errors.length > 0 && (
            <details className="mt-4 text-xs">
              <summary className="cursor-pointer text-neutral-600">Ver {status.errors.length} avisos/erros parciais</summary>
              <ul className="mt-2 space-y-1 font-mono text-[11px] text-neutral-500 max-h-60 overflow-auto">
                {status.errors.map((e, i) => <li key={i}>· {e}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
